// src/pages/happinessreport/components/ComparativeCharts.tsx
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tooltip, OverlayTrigger } from 'react-bootstrap';
import styled from 'styled-components';
import MonthReportLineChart from '../../../components/dashboard/lineChart';
import { ContainerOverflowCharts } from '../styles';
import { LineChartSeriesInterface } from '../../../interfaces/chartSeries.interface';
import { FormattedDimensionResults } from '../../../interfaces/dimension.interface';
import { StyledCardBodyOverflow, StyledCardHeader } from '../../../components/dashboard/attentionPoints/styles';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { FormControl, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../i18n/useAppLocale';
import { representationAgeRangeOptions } from '../../../services/utils/representationOptions';

const ResponsiveCardHeader = styled(StyledCardHeader)`
  min-width: 1000px;
`;

const GroupChartHeader = styled(ResponsiveCardHeader)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    flex-direction: c
  }

  @media (max-width: 420px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ContainerPaginationDivision= styled.div`
  display: flex; 
  /* align-items: center; */
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;

  }

  @media (max-width: 420px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ContainerArrows= styled.div`
  display: flex;
  gap: 1rem; 

  @media (max-width: 768px) {
    align-items: flex-start;
  }

  @media (max-width: 420px) {
    gap: 2em;
  }
`;

type GeneralDataShape = {
  generalEpDimension?: FormattedDimensionResults | null;
  generalEngDimension?: FormattedDimensionResults | null;
  generalRelDimension?: FormattedDimensionResults | null;
  generalSigDimension?: FormattedDimensionResults | null;
  generalReaDimension?: FormattedDimensionResults | null;
  generalVitDimension?: FormattedDimensionResults | null;
};

type FilteredDataShape = {
  filteredEpDimension?: FormattedDimensionResults | null;
  filteredEngDimension?: FormattedDimensionResults | null;
  filteredRelDimension?: FormattedDimensionResults | null;
  filteredSigDimension?: FormattedDimensionResults | null;
  filteredReaDimension?: FormattedDimensionResults | null;
  filteredVitDimension?: FormattedDimensionResults | null;
};

type Props = {
  loadingSexesData?: boolean;
  loadingAgeRangesData?: boolean;
  loadingGroupsData?: boolean;
  genderSeries?: LineChartSeriesInterface[];
  ageRangesSeries?: LineChartSeriesInterface[];
  groupsSeries?: LineChartSeriesInterface[];
  generalData?: GeneralDataShape | null;
  filteredData?: FilteredDataShape | null;
  hasFilter?: boolean;
};

const SmallTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h6 style={{ margin: 0, color: '#3C4043' }}>{children}</h6>
);

/**
 * ComparativeCharts
 * - Recebe agora generalData e filteredData do controller e repassa ao chart.
 * - Garante que cada série tenha `label` (fallback '-').
 */
const ComparativeCharts: React.FC<Props> = ({
  loadingSexesData,
  loadingAgeRangesData,
  loadingGroupsData,
  genderSeries = [],
  ageRangesSeries = [],
  groupsSeries = [],
  generalData = null,
  filteredData = null,
  hasFilter = false
}) => {
  const { t } = useTranslation('reports');
  const { t: ti } = useTranslation('insights');
  const appLocale = useAppLocale();
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOrder, setSortOrder] = useState<'alpha-asc' | 'alpha-desc' | 'ifoz-asc' | 'ifoz-desc'>('alpha-asc');
  const groupsPerPage = 5;

  useEffect(() => {
    setCurrentPage(0);
  }, [sortOrder]);

  const localizedFilterSuffix = t('filterSuffix');
  const canonicalFilterSuffix = ' (com filtro)';
  const suffixExceptions = ['IBFOZ Geral', 'Seu filtro'];

  const ensureLabel = (list: LineChartSeriesInterface[]) =>
    list.map(s => {
      const sourceLabel = s.label ?? '-';
      const canonicalLabel = sourceLabel.endsWith(canonicalFilterSuffix)
        ? sourceLabel.slice(0, -canonicalFilterSuffix.length)
        : sourceLabel;
      const normalizedLabel = canonicalLabel.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const sexLabels = ['masculino', 'feminino', 'prefiro nao informar'];
      const ageIndex = representationAgeRangeOptions.findIndex(option => option.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === normalizedLabel);
      const label = normalizedLabel === 'masculino' ? ti('sexOptions.male')
        : normalizedLabel === 'feminino' ? ti('sexOptions.female')
        : sexLabels[2] === normalizedLabel ? ti('sexOptions.preferNot')
        : ageIndex >= 0 ? [ti('ageOptions.under24'), ti('ageOptions.from25to30'), ti('ageOptions.from31to35'), ti('ageOptions.from36to40'), ti('ageOptions.from41to45'), ti('ageOptions.from46to50'), ti('ageOptions.from51to55'), ti('ageOptions.from56to60'), ti('ageOptions.over61')][ageIndex]
        : canonicalLabel === 'IBFOZ Geral' ? t('general')
        : canonicalLabel === 'Seu filtro' ? t('filtered')
        : canonicalLabel;
      const shouldAppendSuffix = hasFilter && !suffixExceptions.includes(canonicalLabel);

      return { ...s, label: shouldAppendSuffix ? `${label}${localizedFilterSuffix}` : label };
    });

  const safeGender = ensureLabel(genderSeries);
  const safeAges = ensureLabel(ageRangesSeries);
  const safeGroups = ensureLabel(groupsSeries);

  const getNullableIfoz = (value?: number | null) => (typeof value === 'number' && !Number.isNaN(value) ? value : null);

  const sortedGroups = [...safeGroups].sort((a, b) => {
    switch (sortOrder) {
      case 'alpha-asc':
        return (a.label ?? '').localeCompare(b.label ?? '', appLocale);
      case 'alpha-desc':
        return (b.label ?? '').localeCompare(a.label ?? '', appLocale);
      case 'ifoz-asc':
        {
          const leftIfOZ = getNullableIfoz(a.ifoz);
          const rightIfOZ = getNullableIfoz(b.ifoz);
          if (leftIfOZ === null && rightIfOZ === null) {
            return (a.label ?? '').localeCompare(b.label ?? '', appLocale);
          }
          if (leftIfOZ === null) return -1;
          if (rightIfOZ === null) return 1;
          const ifozAsc = leftIfOZ - rightIfOZ;
          if (ifozAsc !== 0) return ifozAsc;
          return (a.label ?? '').localeCompare(b.label ?? '', appLocale);
        }
      case 'ifoz-desc':
        {
          const leftIfOZ = getNullableIfoz(a.ifoz);
          const rightIfOZ = getNullableIfoz(b.ifoz);
          if (leftIfOZ === null && rightIfOZ === null) {
            return (a.label ?? '').localeCompare(b.label ?? '', appLocale);
          }
          if (leftIfOZ === null) return 1;
          if (rightIfOZ === null) return -1;
          const ifozDesc = rightIfOZ - leftIfOZ;
          if (ifozDesc !== 0) return ifozDesc;
          return (a.label ?? '').localeCompare(b.label ?? '', appLocale);
        }
      default:
        return 0;
    }
  });

  const totalPages = Math.ceil(sortedGroups.length / groupsPerPage);
  const startIndex = currentPage * groupsPerPage;
  const endIndex = startIndex + groupsPerPage;
  const paginatedGroups = sortedGroups.slice(startIndex, endIndex);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages - 1) {
      setCurrentPage(0);
    }
  }, [totalPages, currentPage]);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // obtemos os general* diretamente do generalData (se houver)
  const generalEpData = generalData?.generalEpDimension ?? null;
  const generalEngData = generalData?.generalEngDimension ?? null;
  const generalRelData = generalData?.generalRelDimension ?? null;
  const generalSigData = generalData?.generalSigDimension ?? null;
  const generalReaData = generalData?.generalReaDimension ?? null;
  const generalVitData = generalData?.generalVitDimension ?? null;

  // se houver dados filtrados relevantes (opcional), podemos passá-los também
  const filteredEpData = filteredData?.filteredEpDimension ?? null;
  const filteredEngData = filteredData?.filteredEngDimension ?? null;
  const filteredRelData = filteredData?.filteredRelDimension ?? null;
  const filteredSigData = filteredData?.filteredSigDimension ?? null;
  const filteredReaData = filteredData?.filteredReaDimension ?? null;
  const filteredVitData = filteredData?.filteredVitDimension ?? null;

  return (
    <div style={{ marginTop: '2em' }}>
      <Row className='pb-2'>
        <Col>
          <StyledCardBodyOverflow style={{ marginBottom: '1em', background: '#FFF', color: '#3C4043', overflowX: 'auto' }}>
            <ResponsiveCardHeader style={{ padding: '1rem 1.25rem', borderBottom: '2px solid #e7e8e9' }}>
              <h5 className='pb-0 mb-0'>{t('comparisonSex')}</h5>
            </ResponsiveCardHeader>
            <Card.Body>
              <ContainerOverflowCharts style={{ marginTop: '.75em' }}>
                {loadingSexesData ? (
                  <div>{t('loading')}</div>
                ) : (safeGender && safeGender.length > 0) ? (
                  <MonthReportLineChart
                    chartId="comparative-sex-chart"
                    heightSize='295px'
                    topPosition='14.5%'
                    heightSizeResponsive='275px'
                    topPositionResponsive='18.85%'

                    // <-- linhas do IBFOZ geral (agora passamos os dados reais)
                    generalEpData={generalEpData}
                    generalEngData={generalEngData}
                    generalRelData={generalRelData}
                    generalSigData={generalSigData}
                    generalReaData={generalReaData}
                    generalVitData={generalVitData}

                    // caso queira também exibir o IBFOZ filtrado, passe filtered* (opcional)
                    filteredEpData={filteredEpData}
                    filteredEngData={filteredEngData}
                    filteredRelData={filteredRelData}
                    filteredSigData={filteredSigData}
                    filteredReaData={filteredReaData}
                    filteredVitData={filteredVitData}

                    // as séries comparativas
                    genderSeries={safeGender}
                  />
                ) : (
                  <div>{t('empty')}</div>
                )}
              </ContainerOverflowCharts>
            </Card.Body>
          </StyledCardBodyOverflow>
        </Col>
      </Row>

      <Row className='pb-2'>
        <Col>
          <StyledCardBodyOverflow style={{ marginBottom: '1em', background: '#FFF', color: '#3C4043', overflowX: 'auto' }}>
            <ResponsiveCardHeader style={{ padding: '1rem 1.25rem', borderBottom: '2px solid #e7e8e9' }}>
              <h5 className='pb-0 mb-0'>{t('comparisonAge')}</h5>
            </ResponsiveCardHeader>

            <Card.Body>
              <ContainerOverflowCharts style={{ marginTop: '.75em' }}>
                {loadingAgeRangesData ? (
                  <div>{t('loading')}</div>
                ) : (safeAges && safeAges.length > 0) ? (
                  <MonthReportLineChart
                    chartId="comparative-age-chart"
                    heightSize='295px'
                    topPosition='14.5%'
                    heightSizeResponsive='275px'
                    topPositionResponsive='18.85%'

                    generalEpData={generalEpData}
                    generalEngData={generalEngData}
                    generalRelData={generalRelData}
                    generalSigData={generalSigData}
                    generalReaData={generalReaData}
                    generalVitData={generalVitData}

                    filteredEpData={filteredEpData}
                    filteredEngData={filteredEngData}
                    filteredRelData={filteredRelData}
                    filteredSigData={filteredSigData}
                    filteredReaData={filteredReaData}
                    filteredVitData={filteredVitData}

                    ageRangesSeries={safeAges}
                  />
                ) : (
                  <div>{t('empty')}</div>
                )}
              </ContainerOverflowCharts>
            </Card.Body>
          </StyledCardBodyOverflow>
        </Col>
      </Row>

      <Row className='pb-2'>
        <Col>
          <StyledCardBodyOverflow style={{ marginBottom: '1em', background: '#FFF', color: '#3C4043', overflowX: 'auto' }}>
            <GroupChartHeader style={{ padding: '1rem 1.25rem', borderBottom: '2px solid #e7e8e9' }}>
              <h5 className='pb-0 mb-0'>{t('comparisonGroups')}</h5>
              {sortedGroups.length > 0 && (
                <ContainerPaginationDivision>
                  <PaginationContainer>
                    <span style={{ marginRight: '1rem', color: '#5f6368', fontSize: '0.875rem' }}>
                      {t('showingGroups', { start: startIndex + 1, end: Math.min(endIndex, sortedGroups.length), total: sortedGroups.length })}
                    </span>

                    <ContainerArrows>
                      <OverlayTrigger placement="top" overlay={<Tooltip>{t('previous')}</Tooltip>}>
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 0}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem 0em', color: currentPage === 0 ? '#adb5bd' : '#202124' }}
                        >
                          <BsArrowLeft />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger placement="top" overlay={<Tooltip>{t('next')}</Tooltip>}>
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage >= totalPages - 1}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem 0em', color: currentPage >= totalPages - 1 ? '#adb5bd' : '#202124' }}
                        >
                          <BsArrowRight />
                        </button>
                      </OverlayTrigger>
                    </ContainerArrows>
                    
                  </PaginationContainer>
                  <FormControl sx={{ minWidth: 220, border: '1px solid #e7e8e9', borderRadius: '6px', color: '#3C4043', fontSize: '16px' }} size="small">
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'alpha-asc' | 'alpha-desc' | 'ifoz-asc' | 'ifoz-desc')}
                    displayEmpty
                    inputProps={{ 'aria-label': String(t('sortOrder')) }}
                  >
                    <MenuItem value="alpha-asc">{t('alphaAsc')}</MenuItem>
                    <MenuItem value="alpha-desc">{t('alphaDesc')}</MenuItem>
                    <MenuItem value="ifoz-desc">{t('bestWorst')}</MenuItem>
                    <MenuItem value="ifoz-asc">{t('worstBest')}</MenuItem>
                  </Select>
                  </FormControl>
                </ContainerPaginationDivision>
              )}
            </GroupChartHeader>

            <Card.Body>
              <ContainerOverflowCharts style={{ marginTop: '.75em' }}>
                {loadingGroupsData ? (
                  <div>{t('loading')}</div>
                ) : (paginatedGroups && paginatedGroups.length > 0) ? (
                  <MonthReportLineChart
                    chartId="comparative-group-chart"
                    heightSize='295px'
                    topPosition='14.5%'
                    heightSizeResponsive='275px'
                    topPositionResponsive='18.85%'

                    generalEpData={generalEpData}
                    generalEngData={generalEngData}
                    generalRelData={generalRelData}
                    generalSigData={generalSigData}
                    generalReaData={generalReaData}
                    generalVitData={generalVitData}

                    filteredEpData={filteredEpData}
                    filteredEngData={filteredEngData}
                    filteredRelData={filteredRelData}
                    filteredSigData={filteredSigData}
                    filteredReaData={filteredReaData}
                    filteredVitData={filteredVitData}

                    groupsSeries={paginatedGroups}
                  />
                ) : (
                  <div>{t('empty')}</div>
                )}
              </ContainerOverflowCharts>
            </Card.Body>
          </StyledCardBodyOverflow>
        </Col>
      </Row>
    </div>
  );
};

export default ComparativeCharts;
